import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  useTheme,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import {
  WbButton,
  WbDrawer,
  WbDrawerContent,
  WbTooltip,
  customAlertApiRef,
  getOnHoverScrollbarStyle,
} from '@agilelab/plugin-wb-platform';
import {
  sleep,
  snakeCaseToTitleCase,
} from '@agilelab/plugin-wb-platform-common';
import { Progress } from '@backstage/core-components';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { ActionData } from '@agilelab/plugin-wb-startup-actions-common';
import { RunningIcon } from '@agilelab/plugin-wb-builder-catalog';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { useApi } from '@backstage/core-plugin-api';
import { startupActionsApiRef } from '../apis';
import { poll } from '@agilelab/plugin-wb-platform-common';

interface StartupActionsModalContextType {
  closeModal: () => void;
  isOpen: boolean;
}

const useStyle = makeStyles(theme => ({
  modalBox: {
    borderRadius: 8,
    boxShadow: theme.shadows[24],
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      maxHeight: '95vh',
    },
  },
  contentContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
    rowGap: 16,
    [theme.breakpoints.down('sm')]: {
      rowGap: 8,
      padding: theme.spacing(1),
    },
    ...getOnHoverScrollbarStyle(theme, false),
  },
  modalTitle: {
    width: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  modalTitleContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  dialogActions: {
    flexShrink: 0,
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
}));

export const StartupActionsModal = ({
  closeModal,
  isOpen,
}: StartupActionsModalContextType) => {
  const theme = useTheme();
  const classes = useStyle();

  const startupActionsApi = useApi(startupActionsApiRef);
  const alertApi = useApi(customAlertApiRef);
  const [drawerContent, setDrawerContent] = useState<JSX.Element | undefined>(
    undefined,
  );
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const [actions, setActions] = useState<ActionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [localAbortController, setLocalAbortController] =
    useState<AbortController | null>(null);
  const [isPolling, setIsPolling] = useState(false); // Track if a poll is active

  const isFinalized = useCallback(
    (inputActions?: ActionData[]) =>
      inputActions?.every(action =>
        ['COMPLETED', 'FAILED', 'CANCELED'].includes(action.status),
      ) ?? false,
    [],
  );

  const startPolling = useCallback(
    async (controller: AbortController) => {
      setIsPolling(true); // Mark polling as active
      poll({
        fn: async () => {
          try {
            const response = await startupActionsApi.getActions();
            return response.actions.toSorted((a, b) =>
              a.name.localeCompare(b.name),
            );
          } catch (error) {
            return undefined;
          }
        },
        finishCondition: isFinalized,
        timeoutMs: 5 * 60 * 1000, // 5 minutes
        onTimeout: () => {
          alertApi.post({
            message:
              'Timeout while fetching startup actions status. Please try again later.',
            severity: 'error',
          });
          setLoading(false);
          setIsPolling(false);
        },
        onFinish: actionData => {
          if (actionData) {
            setActions(actionData);
            setLoading(false);
          }
          setIsPolling(false);
        },
        onUpdate: actionData => {
          if (actionData) {
            setActions(actionData);
            setLoading(false);
          }
        },
        signal: controller.signal,
        ms: 2000,
      });
    },
    [startupActionsApi, alertApi, isFinalized],
  );

  useEffect(() => {
    if (isOpen) {
      if (!isPolling) {
        const controller = new AbortController();
        setLocalAbortController(controller);
        startPolling(controller);
      }
    } else {
      localAbortController?.abort();
      setLocalAbortController(null);
      setIsPolling(false);
    }

    return () => {
      localAbortController?.abort();
      setIsPolling(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, startPolling, localAbortController]);

  const handleAutoRenew = async () => {
    if (isPolling) {
      return;
    }

    try {
      setLoading(true);
      await startupActionsApi.triggerActions();
      await sleep(1000); // Small delay to ensure backend starts processing
      const controller = new AbortController();
      setLocalAbortController(controller);
      startPolling(controller);
    } catch (error) {
      alertApi.post({
        message: 'Failed to trigger startup actions. Please try again.',
        severity: 'error',
      });
      setLoading(false);
    }
  };

  const handleIconClick = (action: ActionData) => {
    if (action.status === 'FAILED' || action.status === 'RETRY_FAILED') {
      setDrawerContent(
        <Box p={2} flexWrap="wrap">
          <Typography variant="h6">Error</Typography>
          <Typography variant="body2" color="error">
            {action.error || 'No error message provided.'}
          </Typography>
        </Box>,
      );
      setDrawerOpen(true);
    }
  };

  const getStatusIcon = (action: ActionData) => {
    const { status } = action;

    switch (status) {
      case 'COMPLETED':
        return (
          <CheckCircleOutlineIcon
            style={{ color: theme.palette.success.main }}
          />
        );
      case 'RETRY_FAILED':
        return (
          <ErrorOutlineIcon style={{ color: theme.palette.warning.main }} />
        );
      case 'IN_PROGRESS':
        return <RunningIcon style={{ color: theme.palette.accent.main }} />;
      case 'FAILED':
        return (
          <RadioButtonCheckedIcon style={{ color: theme.palette.error.main }} />
        );
      case 'CANCELED':
      default:
        return <RemoveCircleIcon style={{ color: theme.palette.grey[500] }} />;
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          'aria-label': 'startup-actions-dialog',
          className: classes.modalBox,
        }}
      >
        <DialogTitle className={classes.modalTitle}>
          <Box className={classes.modalTitleContent}>
            <Typography>Startup Actions Status</Typography>
            <WbTooltip title="Close">
              <IconButton
                aria-label="auto-renew"
                onClick={handleAutoRenew}
                disabled={!isFinalized(actions) || loading}
              >
                <AutorenewIcon />
              </IconButton>
            </WbTooltip>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.contentContainer}>
          {loading ? (
            <Progress />
          ) : (
            actions.map(action => (
              <Box key={action.name} className={classes.actionBox}>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {action.name}
                </Typography>
                <Box display="flex" alignItems="center">
                  <WbTooltip
                    title={`Updated at: ${new Date(
                      action.updated_at,
                    ).toLocaleString()}`}
                  >
                    {action.status === 'FAILED' ||
                    action.status === 'RETRY_FAILED' ? (
                      <IconButton
                        size="small"
                        aria-label="step"
                        disableRipple
                        onClick={() => handleIconClick(action)}
                      >
                        {getStatusIcon(action)}
                      </IconButton>
                    ) : (
                      getStatusIcon(action)
                    )}
                  </WbTooltip>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ minWidth: '10vw', textAlign: 'right' }}
                  >
                    {snakeCaseToTitleCase(action.status)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <WbButton onClick={closeModal} variant="outlined" label="Close" />
        </DialogActions>
      </Dialog>

      <WbDrawer
        title="Action Details"
        open={isDrawerOpen}
        setOpen={() => setDrawerOpen(!isDrawerOpen)}
      >
        <WbDrawerContent>{drawerContent}</WbDrawerContent>
      </WbDrawer>
    </>
  );
};

export default StartupActionsModal;
